import type {
	AppMenuConfig,
	MenuItemConfig,
	MenuSectionConfig,
} from '@atlassian/universal-create-common/bin-param';

import { DEFAULT_OVERFLOW_THRESHOLD } from '../../../../types/menu';
import type { MenuItemV2, MenuSectionV2 } from '../../../../types/menuV2';

import { isNativeMenuItem } from './itemTypeGuards';

/**
 * Converts a MenuItemV2 to MenuItemConfig format for the BFF API.
 */
export const convertItemToConfig = (item: MenuItemV2): MenuItemConfig => ({
	type: isNativeMenuItem(item) ? 'native' : item.type,
	ati: item.ati.toString(),
	promoted: item?.promoted,
});

/**
 * Converts a MenuSectionV2 to MenuSectionConfig format for the BFF API.
 */
export const convertSectionToConfig = (section: MenuSectionV2): MenuSectionConfig => {
	if (section.type === 'suggestionSection') {
		return {
			type: 'suggestionSection' as const,
			limit: section.limit ?? 5,
			...(section.excludeAtis && { excludeAtis: section.excludeAtis.map((ati) => ati.toString()) }),
			...(section.preferredAtis && {
				preferredAtis: section.preferredAtis.map((ati) => ati.toString()),
			}),
		};
	}

	if (section.type === 'appDefinedSection') {
		const overflowThreshold = Boolean(section.foldable)
			? typeof section.foldable === 'number'
				? section.foldable
				: DEFAULT_OVERFLOW_THRESHOLD
			: undefined;
		return {
			type: 'appDefinedSection' as const,
			items: section.items.map(convertItemToConfig),
			personalize: section.personalize,
			...(overflowThreshold && { overflowThreshold }),
		};
	}

	// Assert that we have handled all possible section types
	const exhaustive: never = section;
	return exhaustive;
};

/**
 * Converts sections configuration to AppMenuConfig format for the BFF API.
 */
export const sectionsToAppMenuConfig = (sections: MenuSectionV2[]): AppMenuConfig => ({
	appMenuConfig: sections.map(convertSectionToConfig),
});
